/**
 * pendingToasts — tiny localStorage-backed queue for "I want a toast to
 * appear in the next screen the user lands on" payloads.
 *
 * Used by Phase D to bridge `PaymentSuccess` (which is its own full-page
 * route, with no Room/Home toast queue mounted) to `Home` / `Room`. When
 * the user taps Continue and lands back on Home, Home drains the queue
 * and pushes each entry into the local UnlockToast queue.
 *
 * Entries survive a tab reload — that's deliberate. Stripe's webhook can
 * arrive while the user is on /payment-success; if the user closes the
 * tab and reopens later, the next session's Home should still announce
 * the granted lanterns.
 */

const KEY = 'tt:pendingToasts';

export interface PendingToast {
  /** Unique-enough id for dedupe. */
  id: string;
  /** Optional structured kind for callers that want different glyphs. */
  kind?: 'currency' | 'season' | 'cosmetic' | 'achievement' | 'level' | 'unlock';
  title: string;
  subtitle?: string;
  rewardChip?: string;
  /** Single-character glyph rendered left of the title. */
  icon?: string;
  /** Tier 1 = small auto-dismiss, 2 = sticky, 3 = full-takeover. */
  tier?: 1 | 2 | 3;
  /** Rarity 1..4 maps to the visual sheen on UnlockToast. */
  rarity?: 1 | 2 | 3 | 4;
  /** When the entry was queued, for staleness pruning. */
  ts: number;
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
const MAX_QUEUE = 16;

function readRaw(): PendingToast[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((t): t is PendingToast =>
      !!t && typeof t === 'object'
      && typeof (t as PendingToast).id === 'string'
      && typeof (t as PendingToast).title === 'string'
      && typeof (t as PendingToast).ts === 'number'
      && now - (t as PendingToast).ts < MAX_AGE_MS,
    );
  } catch {
    return [];
  }
}

function writeRaw(list: PendingToast[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_QUEUE)));
  } catch { /* */ }
}

export function pushPendingToast(entry: Omit<PendingToast, 'ts'>): void {
  const cur = readRaw();
  // Dedupe by id — repeated PaymentSuccess polling shouldn't enqueue the
  // same "+1200 lanterns" twice.
  if (cur.some((t) => t.id === entry.id)) return;
  cur.push({ ...entry, ts: Date.now() });
  writeRaw(cur);
}

/** Pull and clear the pending queue. Caller is expected to push each
 *  entry into the active toast UI immediately. */
export function drainPendingToasts(): PendingToast[] {
  const cur = readRaw();
  if (cur.length === 0) return [];
  try { localStorage.removeItem(KEY); } catch { /* */ }
  return cur;
}
